<div class="kt-portlet">
	<kt-portlet-header [sticky]="false" [title]="'Card Reader'" [class]="'kt-portlet__head--lg'">
	</kt-portlet-header>
	<div class="kt-portlet__body">
		<div class="card-reader col-xl-10 col-md-12 col-sm-12 mt-3 mx-auto">
			<mat-progress-bar *ngIf="isLoading || isBusy || isCampusLoading" mode="indeterminate"></mat-progress-bar>

			<div class="card-reader__error" role="alert" *ngIf="errorMessage">{{ errorMessage }}</div>

			<div class="card-reader__status card-reader__status--mismatch" role="alert" *ngIf="undeletedReaderId">
				<mat-icon aria-hidden="true">error_outline</mat-icon>
				<div class="card-reader__status-text">
					<p class="card-reader__title">Reader {{ undeletedReaderId }} is still in Stripe</p>
					<p>It’s off this machine, but it can’t be registered again until Stripe deletes it.</p>
				</div>
				<button mat-stroked-button type="button" [disabled]="isBusy" (click)="retryDelete()">Delete again</button>
			</div>

			<section class="card-reader__section" aria-labelledby="card-reader-reader-heading">
				<h3 id="card-reader-reader-heading" class="card-reader__section-title">Card reader</h3>

				<!-- No reader -->
				<div *ngIf="!isLoading && mode === 'none'" class="card-reader__empty">
					<p class="card-reader__title">No card reader on this machine</p>
					<p class="card-reader__muted">Card payments stay off until a reader is registered and its settings reach the machine.</p>
					<ng-container *ngIf="machineStatus?.kind === 'stripe_on'">
						<ng-container *ngTemplateOutlet="machineStatusTpl"></ng-container>
					</ng-container>
					<div class="card-reader__actions">
						<button mat-raised-button color="primary" type="button" (click)="startRegistration()">Register a reader</button>
					</div>
				</div>

				<!-- Step 1: confirm the campus the reader will be registered to -->
				<div *ngIf="mode === 'confirm' && !isCampusLoading && campusResult" class="card-reader__form">
					<ng-container *ngIf="!campus">
						<div class="card-reader__status card-reader__status--mismatch" role="alert">
							<mat-icon aria-hidden="true">error_outline</mat-icon>
							<div class="card-reader__status-text">
								<p class="card-reader__title">This machine has no campus</p>
								<p>A reader is registered to the machine’s campus. Set the campus in Machine Details, then come back.</p>
							</div>
						</div>
						<div class="card-reader__actions">
							<a mat-raised-button color="primary" [routerLink]="['/machine', machineId, 'details']">Machine Details</a>
							<button mat-stroked-button type="button" (click)="cancel()">Cancel</button>
						</div>
					</ng-container>

					<ng-container *ngIf="campus">
						<p class="card-reader__title">Is this the machine’s permanent campus?</p>
						<div class="card-reader__campus">
							<span class="card-reader__name">{{ campus.name || ('Campus ' + campus.campus_id) }}</span>
							<span class="card-reader__muted" *ngIf="campus.client_name">{{ campus.client_name }}</span>
							<span class="card-reader__muted" *ngFor="let line of campusAddressLines">{{ line }}</span>
						</div>
						<p class="card-reader__muted">
							The reader is registered to this campus in Stripe. If the machine moves to another campus later, the reader has to be registered again.
						</p>

						<div class="card-reader__status card-reader__status--mismatch" role="alert" *ngIf="!campusResult.address_complete">
							<mat-icon aria-hidden="true">error_outline</mat-icon>
							<div class="card-reader__status-text">
								<p class="card-reader__title">This campus’s address is incomplete</p>
								<p>Stripe needs a street address, city, state, ZIP code, and country. Update the campus, then come back.</p>
							</div>
							<a mat-stroked-button [routerLink]="['/clients', 'edit-campus', campus.campus_id]">Edit campus</a>
						</div>

						<div class="card-reader__actions">
							<button mat-raised-button color="primary" type="button" *ngIf="campusResult.address_complete" (click)="confirmCampus()">
								Yes, this is its campus
							</button>
							<a mat-stroked-button [routerLink]="['/machine', machineId, 'details']">No, change it in Machine Details</a>
							<button mat-button type="button" (click)="cancel()">Cancel</button>
						</div>
					</ng-container>
				</div>

				<!-- Step 2: the registration code -->
				<form *ngIf="mode === 'register'" class="card-reader__form" (ngSubmit)="registerReader()">
					<div class="card-reader__context">
						<span class="card-reader__context-label">Campus</span>
						<span>{{ campusName }}<span class="card-reader__muted-inline" *ngIf="campus?.client_name"> · {{ campus.client_name }}</span></span>
						<button mat-button type="button" class="card-reader__inline-button" [disabled]="isBusy" (click)="mode = 'confirm'">Change</button>
					</div>

					<ol class="card-reader__steps">
						<li>
							<span class="card-reader__step-title">Get a code from the reader</span>
							<span class="card-reader__muted">Swipe left › admin password › <strong>Generate pairing code</strong></span>
						</li>
						<li>
							<span class="card-reader__step-title">Enter the code</span>
							<mat-form-field class="card-reader__code-field" floatLabel="never">
								<input matInput name="registrationCode" placeholder="e.g. apple-river-stone" [(ngModel)]="registrationCode"
									[disabled]="isBusy" autocomplete="off" autocapitalize="off" spellcheck="false"
									aria-label="Registration code">
								<mat-hint>Three words joined by hyphens</mat-hint>
							</mat-form-field>
						</li>
					</ol>

					<p class="card-reader__note">
						<mat-icon aria-hidden="true">info_outline</mat-icon>
						<span>Named <strong>{{ readerLabel }}</strong> in Stripe. The machine restarts about a minute after you register.</span>
					</p>

					<div class="card-reader__actions">
						<button mat-raised-button color="primary" type="submit"
							[disabled]="isBusy || !registrationCode.trim() || !confirmedCampusId">
							{{ isBusy ? 'Registering…' : 'Register and assign' }}
						</button>
						<button mat-stroked-button type="button" [disabled]="isBusy" (click)="cancel()">Cancel</button>
					</div>
				</form>

				<!-- Reader assigned -->
				<div *ngIf="!isLoading && mode === 'assigned' && reader" class="card-reader__assigned">
					<div class="card-reader__status card-reader__status--in_use" role="status" *ngIf="justRegistered">
						<mat-icon aria-hidden="true">check_circle</mat-icon>
						<div class="card-reader__status-text">
							<p class="card-reader__title">Reader registered</p>
							<p>The machine restarts in about a minute to start using it.</p>
						</div>
						<button mat-button type="button" (click)="justRegistered = false">Dismiss</button>
					</div>
					<div class="card-reader__summary">
						<span>
							<span class="card-reader__name">{{ readerName(reader) }}</span>
							<span class="card-reader__muted">{{ formatDeviceType(reader.device_type) }} · {{ reader.serial_number || reader.stripe_reader_id }}</span>
						</span>
						<span class="card-reader__pill" *ngIf="reader.in_stripe !== false" [class.card-reader__pill--on]="isOnline(reader)">{{ reader.status || 'unknown' }}</span>
					</div>

					<div class="card-reader__status card-reader__status--mismatch" role="alert" *ngIf="reader.in_stripe === false">
						<mat-icon aria-hidden="true">error_outline</mat-icon>
						<div class="card-reader__status-text">
							<p class="card-reader__title">This reader isn’t in Stripe</p>
							<p>It may have been deleted or registered to another Stripe account. Remove it, then register the reader again.</p>
						</div>
					</div>

					<div class="card-reader__status card-reader__status--mismatch" role="alert" *ngIf="readerInWrongLocation">
						<mat-icon aria-hidden="true">error_outline</mat-icon>
						<div class="card-reader__status-text">
							<p class="card-reader__title">Registered to another campus’s Stripe location</p>
							<p>Stripe can’t move a reader between campuses. To register it to {{ reader.assignment?.campus_name || 'this machine’s campus' }}, delete it from Stripe, then enter a new code from the reader.</p>
						</div>
						<button mat-stroked-button type="button" *ngIf="!confirmReregister" [disabled]="isBusy" (click)="confirmReregister = true">
							Re-register
						</button>
					</div>
					<div class="card-reader__confirm" *ngIf="confirmReregister">
						<p>This removes the reader from this machine, which turns card payments off, and deletes it in Stripe. You’ll need to be at the reader to get a new registration code. The machine restarts after the reader is removed, and again after it’s registered.</p>
						<div class="card-reader__actions">
							<button mat-raised-button color="warn" type="button" [disabled]="isBusy" (click)="reregisterReader()">Delete and re-register</button>
							<button mat-stroked-button type="button" [disabled]="isBusy" (click)="confirmReregister = false">Cancel</button>
						</div>
					</div>

					<ng-container *ngTemplateOutlet="machineStatusTpl"></ng-container>

					<dl class="card-reader__meta">
						<dt>Campus</dt><dd>{{ reader.assignment?.campus_name || '—' }}</dd>
						<dt>Stripe location</dt><dd class="card-reader__mono">{{ reader.location_id || '—' }}</dd>
						<dt>Reader ID</dt><dd class="card-reader__mono">{{ reader.stripe_reader_id }}</dd>
						<dt>Assigned</dt><dd>{{ reader.assignment?.assigned_at ? (reader.assignment.assigned_at | date:'medium') : '—' }}</dd>
					</dl>

					<div class="card-reader__actions" *ngIf="!confirmRemove">
						<button mat-stroked-button type="button" *ngIf="reader.in_stripe !== false" [disabled]="isBusy" (click)="retrySync()">Re-send settings to machine</button>
						<button mat-stroked-button color="warn" type="button" [disabled]="isBusy" (click)="confirmRemove = true">Remove reader</button>
					</div>
					<div class="card-reader__confirm" *ngIf="confirmRemove">
						<p>Card payments turn off on this machine, and the reader is deleted from Stripe. To use it again, register it with a new code from the reader. The machine restarts about a minute later to apply this.</p>
						<div class="card-reader__actions">
							<button mat-raised-button color="warn" type="button" [disabled]="isBusy" (click)="removeReader()">Remove reader</button>
							<button mat-stroked-button type="button" [disabled]="isBusy" (click)="confirmRemove = false">Keep it</button>
						</div>
					</div>
				</div>
			</section>
		</div>
	</div>
</div>

<ng-template #machineStatusTpl>
	<div *ngIf="machineStatus || isStatusLoading" class="card-reader__status" role="status"
		[ngClass]="machineStatus && !isStatusLoading ? 'card-reader__status--' + machineStatus.kind : ''">
		<mat-icon aria-hidden="true">{{ isStatusLoading ? 'sync' : statusIcon }}</mat-icon>
		<div class="card-reader__status-text">
			<p class="card-reader__title">{{ isStatusLoading ? 'Checking the machine…' : machineStatus.title }}</p>
			<p *ngIf="!isStatusLoading && machineStatus.detail">{{ machineStatus.detail }}</p>
		</div>
		<button mat-button type="button" [disabled]="isStatusLoading" (click)="loadMachineStatus()">Check again</button>
	</div>
</ng-template>
